/**
 * Top-of-screen banner shown when the current session was created via
 * BetterAuth's admin-impersonation flow. The banner makes it impossible
 * for an admin to forget they're acting as another user — a real risk
 * when impersonation is used to debug, where an "innocent" click could
 * delete the user's data, change their password, etc.
 *
 * The "Stop impersonating" button calls authClient.admin.stopImpersonating()
 * which restores the admin's own session cookie server-side; we then
 * navigate back to /admin/users so the admin lands somewhere sensible.
 */
import { toast } from 'sonner';
import { Shield } from 'lucide-react';
import { authClient } from '@/shared/auth/authClient';
import { useAuth } from '@/shared/auth/AuthProvider';

export function ImpersonationBanner() {
  const { session, user } = useAuth();

  // Only render when the session-shape's `impersonatedBy` is set.
  // BetterAuth's admin plugin populates this when `/admin/impersonate-user`
  // creates the session.
  const impersonatedBy = (session?.session as { impersonatedBy?: string | null } | undefined)
    ?.impersonatedBy;
  if (!impersonatedBy) return null;

  const targetName = user?.name?.trim() || user?.email?.split('@')[0] || 'this user';

  return (
    <div
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        width: '100%',
        background: 'var(--color-warning-text, #B45309)',
        color: '#fff',
        padding: '8px 16px',
        fontSize: 13,
        fontWeight: 500,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        boxShadow: '0 1px 0 rgba(0,0,0,0.12)',
      }}
      role="status"
      aria-live="polite"
    >
      <Shield size={14} strokeWidth={2.5} />
      <span>
        You are impersonating <strong>{targetName}</strong>
      </span>
      <button
        type="button"
        onClick={async () => {
          const { error } = await authClient.admin.stopImpersonating();
          if (error) {
            toast.error(error.message ?? 'Could not stop impersonating');
            return;
          }
          toast.success('Returned to your admin session');
          // Defer to next tick so the toast actually renders before the
          // page goes away, then do a hard navigation. `replace` instead
          // of `assign` so the back button doesn't bounce the admin back
          // into the impersonation context. Using location.href as a
          // belt-and-braces signal — some edge browser configs swallow
          // assign/replace silently.
          setTimeout(() => {
            window.location.href = '/admin/users';
          }, 50);
        }}
        style={{
          background: 'rgba(255,255,255,0.18)',
          color: '#fff',
          border: '1px solid rgba(255,255,255,0.4)',
          borderRadius: 6,
          padding: '3px 10px',
          fontSize: 12,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        Stop impersonating
      </button>
    </div>
  );
}
